/*
 * Results page search field (KRAKEN-4629): the clear button, and no empty submits.
 * The header fields are driven by assets/search-overlay.js and are not kept in sync
 * with this one.
 */
class MainSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-search-input]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.form = this.querySelector('form');

    if (!this.input || !this.form) return;

    this.input.addEventListener('input', this.syncClearButton.bind(this));
    this.clearButton?.addEventListener('click', this.onClear.bind(this));
    this.form.addEventListener('submit', (event) => {
      if (this.input.value.trim() === '') event.preventDefault();
    });
  }

  syncClearButton() {
    if (this.clearButton) this.clearButton.hidden = this.input.value === '';
  }

  onClear(event) {
    event.preventDefault();
    this.input.value = '';
    this.syncClearButton();
    this.input.focus();
  }
}

customElements.define('main-search', MainSearch);

/*
 * Results page tabs (KRAKEN-4629)
 * -----------------------------------------------------------------------------
 * Products are rendered with the page. Collections (predictive search — the search object
 * has none) and pages are fetched in the background, started by the inline script in
 * sections/main-search.liquid, so switching tabs is instant and never reloads.
 *
 * The strip stays hidden until the counts are in, so labels never jump. A tab with nothing
 * in it is left out; if products found nothing, the first tab that did is opened; the tab
 * travels in `?tab=` (replaceState — tabs are not history steps).
 */
class SearchTabs extends HTMLElement {
  constructor() {
    super();
    this.revealTimeoutMs = 3000;
    this.names = ['products', 'collections', 'pages'];
  }

  connectedCallback() {
    this.page = this.closest('[data-search-page]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    if (!this.page || !this.tabs.length) return;

    this.counts = { products: Number(this.dataset.productsCount || 0), collections: null, pages: null };
    this.requestedTab = this.readTab();

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('pointerover', this.onTabIntent);
    this.addEventListener('pointerdown', this.onTabIntent);
    this.addEventListener('focusin', this.onTabIntent);
    window.addEventListener('popstate', this.onPopState);

    this.select(this.page.dataset.activeTab || 'products', { updateUrl: false });
    this.load();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  readTab() {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return this.names.includes(tab) ? tab : null;
  }

  async load() {
    const timeout = new Promise((resolve) => window.setTimeout(resolve, this.revealTimeoutMs, 'timeout'));
    const panels = Promise.all([
      this.loadPanel('collections', this.dataset.collectionsUrl),
      this.loadPanel('pages', this.dataset.pagesUrl),
    ]);

    // A slow tab must not hold the strip back forever; it fills in when it arrives.
    await Promise.race([panels, timeout]);
    this.reveal();
    panels.then(() => this.syncTabs());
  }

  async loadPanel(name, url) {
    const panel = document.getElementById(`SearchPanel-${name}`);
    if (!panel || !url) return;

    let markup = null;
    try {
      const prefetched = window.cpSearchPrefetch?.[url];
      if (prefetched) {
        markup = await prefetched;
      } else {
        const response = await fetch(url, { credentials: 'same-origin' });
        markup = response.ok ? await response.text() : null;
      }
    } catch (error) {
      markup = null;
    }

    const content = markup
      ? new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-search-panel-content]')
      : null;

    if (!content) {
      // Could not load: leave the tab out rather than offer an empty one.
      this.counts[name] = 0;
      panel.querySelector('[data-panel-loading]')?.remove();
      this.syncTabs();
      return;
    }

    this.counts[name] = Number(content.dataset.count || 0);
    panel.replaceChildren(content);
    if (this.page.dataset.tabsState === 'ready') this.syncTabs();
  }

  reveal() {
    this.syncTabs();

    // Products found nothing and the shopper did not ask for a tab: open the first tab
    // that has something, rather than a dead end with results one click away.
    if (!this.requestedTab && this.counts.products === 0) {
      const next = ['collections', 'pages'].find((name) => this.counts[name] > 0);
      if (next) this.select(next, { updateUrl: false });
    }

    this.page.dataset.tabsState = 'ready';
    this.dataset.state = 'ready';
  }

  syncTabs() {
    const active = this.page.dataset.activeTab;

    this.tabs.forEach((tab) => {
      const name = tab.dataset.tab;
      const count = this.counts[name];
      const countLabel = tab.querySelector('[data-tab-count]');

      if (countLabel && count !== null) countLabel.textContent = `(${count})`;
      // Never take away the tab the shopper is on, even if it came back empty.
      tab.hidden = count === 0 && name !== active;
    });

    const anyResults = this.names.some((name) => this.counts[name] > 0);
    const pending = this.names.some((name) => this.counts[name] === null);
    this.hidden = !anyResults && !pending && active === 'products';
  }

  select(name, { updateUrl = true, focus = false } = {}) {
    if (!this.names.includes(name)) name = 'products';

    this.page.dataset.activeTab = name;
    this.tabs.forEach((tab) => {
      const selected = tab.dataset.tab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });

    if (!updateUrl) return;

    this.requestedTab = name === 'products' ? null : name;

    // GTM wraps history.replaceState and runs its History Change listener inside the call
    // (50–70ms here), so the address is updated after the new tab has painted, not before.
    window.clearTimeout(this.urlTimer);
    requestAnimationFrame(() => {
      this.urlTimer = window.setTimeout(() => {
        const url = new URL(window.location.href);
        if (name === 'products') {
          url.searchParams.delete('tab');
        } else {
          url.searchParams.set('tab', name);
        }
        // Keep history.state: facets.js stores its filter state there for Back.
        window.history.replaceState(window.history.state, '', url.toString());
      });
    });
  }

  onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || tab.hidden) return;

    this.select(tab.dataset.tab);
  };

  // The collection images sit in a hidden panel, where lazy loading holds them back until the
  // switch. Start them as soon as the pointer or focus reaches the tab, so the panel is not
  // grey when it opens — without spending the bandwidth for shoppers who never switch.
  onTabIntent = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;

    document
      .getElementById(`SearchPanel-${tab.dataset.tab}`)
      ?.querySelectorAll('img[loading="lazy"]')
      .forEach((image) => {
        image.loading = 'eager';
      });
  };

  onKeydown = (event) => {
    const visible = this.tabs.filter((tab) => !tab.hidden);
    const index = visible.indexOf(document.activeElement);
    if (index === -1) return;

    let next = null;
    if (event.key === 'ArrowRight') next = visible[(index + 1) % visible.length];
    if (event.key === 'ArrowLeft') next = visible[(index - 1 + visible.length) % visible.length];
    if (event.key === 'Home') next = visible[0];
    if (event.key === 'End') next = visible[visible.length - 1];
    if (!next) return;

    event.preventDefault();
    this.select(next.dataset.tab, { focus: true });
  };

  // facets.js pushes history entries for filters; Back to one of those must show the tab
  // its URL names.
  onPopState = () => {
    this.select(this.readTab() || 'products', { updateUrl: false });
    this.syncTabs();
  };
}

customElements.define('search-tabs', SearchTabs);
